<script setup>
// Вибір реакції на повідомлення, як у Telegram: швидкі, нещодавні, емодзі за
// групами, будь-які смайли каналу (7TV, BTTV, FFZ, Twitch) і поле для
// власного емодзі. Реакція — це емодзі або «:назва» смайла.
//
// Малюємо через Teleport у body: стрічка чату прокручується, і панель
// всередині неї обрізалась би. z-index на одиницю більший за плаваюче вікно
// чату (див. live/Chat.vue), інакше в режимі вікна її не було б видно.

import { EMOJI_GROUPS, QUICK_REACTIONS, emoteReaction, isEmojiReaction } from '~/utils/chatEmoji'

const props = defineProps({
  // Кнопка, біля якої відкриваємось
  anchor: { type: Object, default: null }
})

const emit = defineEmits(['pick', 'close'])

const { list: emoteList, load: loadEmotes } = useEmotes()

// Нещодавні реакції — у браузері глядача, на сервер не їдуть
const RECENT_KEY = 'chat:recentReactions'
const RECENT_MAX = 30

const recent = ref([])

const loadRecent = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
    recent.value = Array.isArray(raw) ? raw.filter((v) => typeof v === 'string').slice(0, RECENT_MAX) : []
  } catch (e) {
    recent.value = []
  }
}

const rememberRecent = (value) => {
  recent.value = [value, ...recent.value.filter((v) => v !== value)].slice(0, RECENT_MAX)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.value))
  } catch (e) {
    // Приватний режим: без збереження обійдемось
  }
}

const tab = ref('emoji')
const group = ref(EMOJI_GROUPS[0].id)
const query = ref('')
const custom = ref('')
const panel = ref(null)
const pos = ref({ left: 0, top: 0, ready: false })

const PANEL_W = 300
const PANEL_H = 320

const place = () => {
  const rect = props.anchor?.getBoundingClientRect()
  if (!rect) return
  const width = Math.min(PANEL_W, window.innerWidth - 16)
  const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8))
  // Знизу місця немає — відкриваємось над кнопкою
  const below = rect.bottom + 4
  const top =
    below + PANEL_H > window.innerHeight - 8 ? Math.max(8, rect.top - PANEL_H - 4) : below
  pos.value = { left, top, ready: true }
}

const onOutside = (event) => {
  if (panel.value?.contains(event.target) || props.anchor?.contains(event.target)) return
  emit('close')
}

const onKey = (event) => {
  if (event.key === 'Escape') emit('close')
}

onMounted(() => {
  loadRecent()
  loadEmotes()
  if (recent.value.length) tab.value = 'recent'
  place()
  document.addEventListener('mousedown', onOutside, true)
  document.addEventListener('keydown', onKey)
  window.addEventListener('resize', place)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onOutside, true)
  document.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', place)
})

const emojis = computed(() => EMOJI_GROUPS.find((g) => g.id === group.value)?.emojis || [])

const EMOTE_LIMIT = 200

const emoteResults = computed(() => {
  const needle = query.value.trim().toLowerCase()
  const base = needle
    ? emoteList.value.filter((e) => e.name.toLowerCase().includes(needle))
    : emoteList.value
  return base.slice(0, EMOTE_LIMIT)
})

const emoteUrl = computed(() => new Map(emoteList.value.map((e) => [e.name, e.url])))

// У нещодавніх і емодзі, і смайли («:назва»); смайл без картинки (список ще
// не завантажився або смайл зник) пропускаємо
const recentItems = computed(() =>
  recent.value.filter((r) => !r.startsWith(':') || emoteUrl.value.has(r.slice(1)))
)

const pick = (reaction) => {
  rememberRecent(reaction)
  emit('pick', reaction)
}

const pickCustom = () => {
  const value = custom.value.trim()
  if (!isEmojiReaction(value)) return
  custom.value = ''
  pick(value)
}

const TABS = [
  ['recent', 'Нещодавні'],
  ['emoji', 'Емодзі'],
  ['emotes', 'Смайли']
]
</script>

<template>
  <Teleport to="body">
    <div
      ref="panel"
      class="z-[2147483001] fixed flex flex-col bg-base-200 shadow-xl border border-base-content/10 rounded-lg overflow-hidden"
      :style="{
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        width: `${PANEL_W}px`,
        maxWidth: 'calc(100vw - 16px)',
        height: `${PANEL_H}px`,
        visibility: pos.ready ? 'visible' : 'hidden'
      }"
      role="dialog"
      aria-label="Реакція на повідомлення"
    >
      <div class="flex items-center gap-1 px-2 pt-2 shrink-0">
        <button
          v-for="emoji in QUICK_REACTIONS"
          :key="emoji"
          type="button"
          class="flex justify-center items-center hover:bg-base-100 rounded w-8 h-8 text-lg"
          @click="pick(emoji)"
        >
          {{ emoji }}
        </button>
      </div>

      <div class="flex mt-1 border-b border-base-content/10 shrink-0">
        <button
          v-for="item in TABS"
          :key="item[0]"
          type="button"
          class="flex-1 px-2 py-1.5 font-semibold text-xs transition-colors"
          :class="tab === item[0] ? 'text-primary border-b-2 border-primary' : 'opacity-60 hover:opacity-90'"
          @click="tab = item[0]"
        >
          {{ item[1] }}
        </button>
      </div>

      <div
        v-if="tab === 'recent'"
        class="flex flex-wrap content-start gap-1 p-2 min-h-0 overflow-y-auto overscroll-contain grow"
      >
        <button
          v-for="item in recentItems"
          :key="item"
          type="button"
          class="flex justify-center items-center hover:bg-base-100 rounded w-8 h-8 text-lg"
          @click="pick(item)"
        >
          <img
            v-if="item.startsWith(':')"
            :src="emoteUrl.get(item.slice(1))"
            :alt="item.slice(1)"
            class="max-w-6 max-h-6 object-contain"
          />
          <template v-else>{{ item }}</template>
        </button>
        <p
          v-if="!recentItems.length"
          class="mx-auto my-4 opacity-50 text-xs"
        >
          Тут зʼявляться ваші останні реакції
        </p>
      </div>

      <template v-else-if="tab === 'emoji'">
        <div class="flex gap-1 px-2 pt-1 shrink-0">
          <button
            v-for="g in EMOJI_GROUPS"
            :key="g.id"
            type="button"
            class="flex justify-center items-center rounded w-8 h-7 text-base transition-colors"
            :class="group === g.id ? 'bg-primary/25' : 'hover:bg-base-100'"
            @click="group = g.id"
          >
            {{ g.icon }}
          </button>
        </div>
        <div class="flex flex-wrap content-start gap-1 p-2 min-h-0 overflow-y-auto overscroll-contain grow">
          <button
            v-for="emoji in emojis"
            :key="emoji"
            type="button"
            class="flex justify-center items-center hover:bg-base-100 rounded w-8 h-8 text-lg"
            @click="pick(emoji)"
          >
            {{ emoji }}
          </button>
        </div>
        <form
          class="flex items-center gap-1.5 p-2 border-t border-base-content/10 shrink-0"
          @submit.prevent="pickCustom"
        >
          <input
            v-model="custom"
            type="text"
            maxlength="24"
            placeholder="Свій емодзі"
            aria-label="Свій емодзі"
            class="flex-1 h-8 text-sm input input-bordered input-xs"
          />
          <button
            type="submit"
            class="btn btn-primary btn-xs"
            :disabled="!isEmojiReaction(custom.trim())"
          >
            OK
          </button>
        </form>
      </template>

      <template v-else>
        <div class="p-2 pb-1 shrink-0">
          <input
            v-model="query"
            type="text"
            placeholder="Пошук смайла"
            class="w-full h-8 text-xs input input-bordered input-xs"
          />
        </div>
        <div
          class="flex flex-wrap content-start gap-1 p-2 min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain grow"
        >
          <button
            v-for="emote in emoteResults"
            :key="emote.name"
            type="button"
            class="flex justify-center items-center hover:bg-base-100 rounded w-8 h-8 shrink-0"
            :title="emote.name"
            @click="pick(emoteReaction(emote.name))"
          >
            <img
              :src="emote.url"
              :alt="emote.name"
              class="max-w-7 max-h-7 object-contain"
              loading="lazy"
              decoding="async"
            />
          </button>
          <p
            v-if="!emoteResults.length"
            class="mx-auto my-4 opacity-50 text-xs"
          >
            Нічого не знайшли
          </p>
        </div>
      </template>
    </div>
  </Teleport>
</template>
